import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, mergeMap, withLatestFrom } from 'rxjs/operators';
import { Troop, UserType } from '../../models';
import { LookupService } from '../../services/lookup.service';
import { ApplicantDocumentType, ApplicationType, CodeValue, Country, County, EducationType, JobVacancySource, LawEnforcementTestType, MilitaryDischargeType, PATLocation, PATResult, Permission, PolygraphDisqualificationReason, Prefix, PsychologicalResult, Race, Role, RolePermission, Sex, StateAddress, Suffix, VeteranPreferenceClaimCategory, WaiverDocument } from './../../../shared/models/lookup.models';
import * as lookupActions from './../actions/lookup.actions';
import * as fromReducer from './../reducers';
import * as fromSelectors from './../selectors/lookup.selectors';


@Injectable()
export class LookupEffects {
  constructor(private lookupService: LookupService,
    private store: Store<fromReducer.State>,
    private actions$: Actions) {
  }



  @Effect()
  getState$: Observable<Action> = this.actions$.pipe(
    ofType(lookupActions.LoadStateApi),
    withLatestFrom(this.store.pipe(select(fromSelectors.IsStateLoaded))),
    filter((action, hasLoaded) => !hasLoaded),
    mergeMap(() =>
      this.lookupService.get.state().pipe(
        mergeMap((states: StateAddress[]) =>
          [
            (lookupActions.LoadStateApiSuccess({ data: states }))
          ])
      )
    )
  );

  @Effect()
  getCounty$: Observable<Action> = this.actions$.pipe(
    ofType(lookupActions.LoadCountyApi),
    withLatestFrom(this.store.pipe(select(fromSelectors.IsCountyLoaded))),
    filter((action, hasLoaded) => !hasLoaded),
    mergeMap(() =>
      this.lookupService.get.county().pipe(
        mergeMap((counties: County[]) =>
          [
            (lookupActions.LoadCountyApiSuccess({ data: counties }))
          ]),
      )
    )
  );

  @Effect()
  getCountry$: Observable<Action> = this.actions$.pipe(
    ofType(lookupActions.LoadCountryApi),
    withLatestFrom(this.store.pipe(select(fromSelectors.IsCountryLoaded))),
    filter((action, hasLoaded) => !hasLoaded),
    mergeMap(() =>
      this.lookupService.get.country().pipe(
        mergeMap((countries: Country[]) =>
          [
            (lookupActions.LoadCountryApiSuccess({ data: countries }))
          ]),
      )
    )
  );

  @Effect()
  getRace$: Observable<Action> = this.actions$.pipe(
    ofType(lookupActions.LoadRaceApi),
    withLatestFrom(this.store.pipe(select(fromSelectors.IsRaceLoaded))),
    filter((action, hasLoaded) => !hasLoaded),
    mergeMap(() =>
      this.lookupService.get.race().pipe(
        mergeMap((race: Race[]) =>
          [
            (lookupActions.LoadRaceApiSucess({ data: race }))
          ]),
      )
    )
  );

  @Effect()
  getSex$: Observable<Action> = this.actions$.pipe(
    ofType(lookupActions.LoadSexApi),
    withLatestFrom(this.store.pipe(select(fromSelectors.IsSexLoaded))),
    filter((action, hasLoaded) => !hasLoaded),
    mergeMap(() =>
      this.lookupService.get.sex().pipe(
        mergeMap((sex: Sex[]) =>
          [
            (lookupActions.LoadSexApiSuccess({ data: sex }))
          ]),
      )
    )
  );

  @Effect()
  getPrefix$: Observable<Action> = this.actions$.pipe(
    ofType(lookupActions.LoadPrefixApi),
    withLatestFrom(this.store.pipe(select(fromSelectors.IsPrefixLoaded))),
    filter((action, hasLoaded) => !hasLoaded),
    mergeMap(() =>
      this.lookupService.get.prefix().pipe(
        mergeMap((prefix: Prefix[]) =>
          [
            (lookupActions.LoadPrefixApiSucess({ data: prefix }))
          ]),
      )
    )
  );

  @Effect()
  getSuffix$: Observable<Action> = this.actions$.pipe(
    ofType(lookupActions.LoadSuffixApi),
    withLatestFrom(this.store.pipe(select(fromSelectors.IsSuffixLoaded))),
    filter((action, hasLoaded) => !hasLoaded),
    mergeMap(() =>
      this.lookupService.get.suffix().pipe(
        mergeMap((suffix: Suffix[]) =>
          [
            (lookupActions.LoadSuffixApiSucess({ data: suffix }))
          ]),
      )
    )
  );

  @Effect()
  getMilitaryDischargeType$: Observable<Action> = this.actions$.pipe(
    ofType(lookupActions.LoadMilitaryDischargeTypeApi),
    withLatestFrom(this.store.pipe(select(fromSelectors.IsMilitaryDischargeTypeLoaded))),
    filter((action, hasLoaded) => !hasLoaded),
    mergeMap(() =>
      this.lookupService.get.militaryDischargeType().pipe(
        mergeMap((militaryDischargeType: MilitaryDischargeType[]) =>
          [
            (lookupActions.LoadMilitaryDischargeTypeApiSucess({data:militaryDischargeType}))
          ]),
      )
    )
  );

  @Effect()
  getJobVacancySource$: Observable<Action> = this.actions$.pipe(
    ofType(lookupActions.LoadJobVacancySourceApi),
    withLatestFrom(this.store.pipe(select(fromSelectors.IsJobVacancySourceLoaded))),
    filter((action, hasLoaded) => !hasLoaded),
    mergeMap(() =>
      this.lookupService.get.jobVacancySource().pipe(
        mergeMap((jobVacancySource: JobVacancySource[]) =>
          [
            (lookupActions.LoadJobVacancySourceApiSucess({data:jobVacancySource}))
          ]),
      )
    )
  );

  @Effect()
  getVeteranPreferenceClaimCategory$: Observable<Action> = this.actions$.pipe(
    ofType(lookupActions.LoadVeteranPreferenceClaimCategoryApi),
    withLatestFrom(this.store.pipe(select(fromSelectors.IsVeteranPreferenceClaimCategoryLoaded))),
    filter((action, hasLoaded) => !hasLoaded),
    mergeMap(() =>
      this.lookupService.get.veteranPreferenceClaimCategory().pipe(
        mergeMap((veteranPreferenceClaimCategory: VeteranPreferenceClaimCategory[]) =>
          [
            (lookupActions.LoadVeteranPreferenceClaimCategoryApiSucess({data:veteranPreferenceClaimCategory}))
          ]),
      )
    )
  );

  @Effect()
  getUserType$: Observable<Action> = this.actions$.pipe(
    ofType(lookupActions.LoadUserTypeApi),
    withLatestFrom(this.store.pipe(select(fromSelectors.IsUserTypeLoaded))),
    filter((action, hasLoaded) => !hasLoaded),
    mergeMap(() =>
      this.lookupService.get.userType().pipe(
        mergeMap((userType: UserType[]) =>
          [
            (lookupActions.LoadUserTypeApiSucess({data:userType}))
          ]),
      )
    )
  );

  @Effect()
  getTroop$: Observable<Action> = this.actions$.pipe(
    ofType(lookupActions.LoadTroopApi),
    withLatestFrom(this.store.pipe(select(fromSelectors.IsTroopLoaded))),
    filter((action, hasLoaded) => !hasLoaded),
    mergeMap(() =>
      this.lookupService.get.troop().pipe(
        mergeMap((troop: Troop[]) =>
          [
            (lookupActions.LoadTroopApiSucess({data:troop}))
          ]),
      )
    )
  );

  @Effect()
  getApplicantDocumentType$: Observable<Action> = this.actions$.pipe(
    ofType(lookupActions.LoadApplicantDocumentTypeApi),
    withLatestFrom(this.store.pipe(select(fromSelectors.IsApplicantDocumentTypeLoaded))),
    filter((action, hasLoaded) => !hasLoaded),
    mergeMap(() =>
      this.lookupService.get.applicantDocumentType().pipe(
        mergeMap((applicantDocumentTypes: ApplicantDocumentType[]) =>
          [
            (lookupActions.LoadApplicantDocumentTypeApiSucess({data:applicantDocumentTypes}))
          ]),
      )
    )
  );

  @Effect()
  getPATLocation$: Observable<Action> = this.actions$.pipe(
    ofType(lookupActions.LoadPATLocationApi),
    mergeMap(() =>
      this.lookupService.get.patLocation().pipe(
        mergeMap((patLocation: PATLocation[]) =>
          [
            (lookupActions.LoadPATLocationApiSucess({data:patLocation}))
          ]),
      )
    )
  );

  @Effect()
  getPolygraphDisqualificationReason$: Observable<Action> = this.actions$.pipe(
    ofType(lookupActions.LoadPolygraphDisqualificationReasonsApi),
    withLatestFrom(this.store.pipe(select(fromSelectors.IsPolygraphDisqualificationReasonLoaded))),
    filter((action, hasLoaded) => !hasLoaded),
    mergeMap(() =>
      this.lookupService.get.patPolygraphDisqualificationReason().pipe(
        mergeMap((polygraphDisqualificationReason: PolygraphDisqualificationReason[]) =>
          [
            (lookupActions.LoadPolygraphDisqualificationReasonsApiSucess({data:polygraphDisqualificationReason}))
          ]),
      )
    )
  );


  @Effect()
  getPsychologicalResult$: Observable<Action> = this.actions$.pipe(
    ofType(lookupActions.LoadPsychologicalResultApi),
    withLatestFrom(this.store.pipe(select(fromSelectors.IsPsychologicalResultLoaded))),
    filter((action, hasLoaded) => !hasLoaded),
    mergeMap(() =>
      this.lookupService.get.psychologicalResult().pipe(
        mergeMap((psychologicalResult: PsychologicalResult[]) =>
          [
            (lookupActions.LoadPsychologicalResultApiSucess({ data: psychologicalResult }))
          ]),
      )
    )
  );

  @Effect()
  getPATResult$: Observable<Action> = this.actions$.pipe(
    ofType(lookupActions.LoadPATResultApi),
    withLatestFrom(this.store.pipe(select(fromSelectors.IsPATResultLoaded))),
    filter((action, hasLoaded) => !hasLoaded),
    mergeMap(() =>
      this.lookupService.get.patResult().pipe(
        mergeMap((patResult: PATResult[]) =>
          [
            (lookupActions.LoadPATResultApiSucess({ data: patResult }))
          ]),
      )
    )
  );

  @Effect()
  getRole$: Observable<Action> = this.actions$.pipe(
    ofType(lookupActions.LoadRoleApi),
    withLatestFrom(this.store.pipe(select(fromSelectors.IsRoleLoaded))),
    filter((action, hasLoaded) => !hasLoaded),
    mergeMap(() =>
      this.lookupService.get.role().pipe(
        mergeMap((roles: Role[]) =>
          [
            (lookupActions.LoadRoleApiSucess({data:roles}))
          ]),
      )
    )
  );

  @Effect()
  getPermission$: Observable<Action> = this.actions$.pipe(
    ofType(lookupActions.LoadPermissionApi),
    withLatestFrom(this.store.pipe(select(fromSelectors.IsPermissionLoaded))),
    filter((action, hasLoaded) => !hasLoaded),
    mergeMap(() =>
      this.lookupService.get.permission().pipe(
        mergeMap((permissions: Permission[]) =>
          [
            (lookupActions.LoadPermissionApiSucess({data:permissions}))
          ]),
      )
    )
  );

  @Effect()
  getRolePermission$: Observable<Action> = this.actions$.pipe(
    ofType(lookupActions.LoadRolePermissionApi),
    withLatestFrom(this.store.pipe(select(fromSelectors.IsPermissionLoaded))),
    filter((action, hasLoaded) => !hasLoaded),
    mergeMap(() =>
      this.lookupService.get.rolePermission().pipe(
        mergeMap((rolePermissions: RolePermission[]) =>
          [
            (lookupActions.LoadRolePermissionApiSucess({data:rolePermissions}))
          ]),
      )
    )
  );


  @Effect()
  getApplicationType$: Observable<Action> = this.actions$.pipe(
    ofType(lookupActions.LoadApplicationTypeApi),
    withLatestFrom(this.store.pipe(select(fromSelectors.IsApplicationTypeLoaded))),
    filter((action, hasLoaded) => !hasLoaded),
    mergeMap(() =>
      this.lookupService.get.applicationType().pipe(
        mergeMap((applicationType: ApplicationType[]) =>
          [
            (lookupActions.LoadApplicationTypeApiSucess({ data: applicationType }))
          ]),
      )
    )
  );


  @Effect()
  getEducationType$: Observable<Action> = this.actions$.pipe(
    ofType(lookupActions.LoadEducationTypeApi),
    withLatestFrom(this.store.pipe(select(fromSelectors.IsEducationTypeLoaded))),
    filter((action, hasLoaded) => !hasLoaded),
    mergeMap(() =>
      this.lookupService.get.educationType().pipe(
        mergeMap((educationTypes: EducationType[]) =>
          [
            (lookupActions.LoadEducationTypeApiSucess({ data: educationTypes }))
          ]),
      )
    )
  );

  @Effect()
  getWaiverDocument$: Observable<Action> = this.actions$.pipe(
    ofType(lookupActions.LoadWaiverDocumentApi),
    withLatestFrom(this.store.pipe(select(fromSelectors.IsWaiverDocumentLoaded))),
    filter((action, hasLoaded) => !hasLoaded),
    mergeMap(() =>
      this.lookupService.get.waiverDocument().pipe(
        mergeMap((waiverDocuments: WaiverDocument[]) =>
          [
            (lookupActions.LoadWaiverDocumentApiSucess({ data: waiverDocuments }))
          ]),
      )
    )
  );

  @Effect()
  getLawEnforcementTestType$: Observable<Action> = this.actions$.pipe(
    ofType(lookupActions.LoadLawEnforcementTestTypeApi),
    withLatestFrom(this.store.pipe(select(fromSelectors.IsLawEnforcementTestTypeLoaded))),
    filter((action, hasLoaded) => !hasLoaded),
    mergeMap(() =>
      this.lookupService.get.lawEnforcementTestType().pipe(
        mergeMap((lawEnforcementTestType: LawEnforcementTestType[]) =>
          [
            (lookupActions.LoadLawEnforcementTestTypeApiSucess({ data: lawEnforcementTestType }))
          ]),
      )
    )
  );

  @Effect()
  getCodeValues$: Observable<Action> = this.actions$.pipe(
    ofType(lookupActions.LoadCodeValuesApi),
    withLatestFrom(this.store.pipe(select(fromSelectors.IsCodeValuesLoaded))),
    filter((action, hasLoaded) => !hasLoaded),
    mergeMap(() =>
      this.lookupService.get.codeValues().pipe(
        mergeMap((codeValues: CodeValue[]) =>
          [
            (lookupActions.LoadCodeValuesApiSucess({ data: codeValues }))
          ]),
      )
    )
  );
}
